import { useEffect, useLayoutEffect } from 'react'
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router'
import { routes } from 'types/routes'
import styles from 'pages/app/App.module.scss'
import EnergyTransitionDocumentContextProvider from 'store/react-context/energy-transition-document/EnergyTransitionDocumentContextProvider.tsx'
import { useStoreDispatch, useStoreSelector } from 'hooks/store.ts'
import { UserTypes } from 'store/auth/types.ts'
import { logout } from 'utils/app.ts'
import { isAuthenticated } from 'utils/localStorageService.ts'
import { getSalesUserData } from 'store/sales/thunks'
import { setUserType } from 'utils/userType.ts'
import { deleteRedirect } from 'store/app/slice.ts'
import { getUserTypeLoginPage } from 'utils/user.ts'
import PageLoading from 'components/page-loading/PageLoading.tsx'
import classNames from 'classnames'
import useGetRouteLayout from 'hooks/useGetRouteLayout.ts'
import { Layouts } from 'layouts/types.ts'
import { popErrorToast } from '@boltenergy-be/design-system'
import { useTranslation } from 'react-i18next'
import Cookies from 'js-cookie'
import { ACCESS_COOKIE_NAME } from 'pages/sales/events/events.constants.ts'
import SalesNavigation from 'layouts/components/navigation/SalesNavigation.tsx'
import { SIDEBAR_WIDTH_EXPANDED, SIDEBAR_WIDTH_VARIABLE } from 'layouts/components/navigation/constants.ts'

const SalesApp = () => {
  // Redux
  const { redirect } = useStoreSelector((store) => store.app)
  const { loading, error } = useStoreSelector((store) => store.sales)
  const dispatch = useStoreDispatch()

  // i18n
  const { t } = useTranslation('sales')

  // React Router
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [query] = useSearchParams()

  // Auth constants
  const authenticatedUserTypes = isAuthenticated()

  // Constants
  const { layout } = useGetRouteLayout()
  const isEditingLayout = layout === Layouts.EDIT_LAYOUT
  const hideNavigation = pathname === routes.SALES_CONTRACTS_ADD || isEditingLayout

  /**
   * Handle things on first render
   */
  useEffect(() => {
    // Redirect to the regular portal or logout if the user is not a sales user
    if (!authenticatedUserTypes[UserTypes.SALES]) {
      logout(() => void navigate(getUserTypeLoginPage(UserTypes.SALES)), UserTypes.SALES)
    }

    // Change the page title
    document.title = 'Bolt Sales Portal'

    // Set the user type
    setUserType(UserTypes.SALES)

    // Fetch the sales user data
    void dispatch(getSalesUserData())
  }, [])

  useEffect(() => {
    if (redirect && redirect.includes('/sales')) {
      void navigate(redirect)
    }
    // Clear redirect from AppStore
    dispatch(deleteRedirect())
  }, [redirect])

  /**
   * Handles the logout with correct redirect
   */
  const handleLogout = () => {
    Cookies.remove(ACCESS_COOKIE_NAME)
    logout(() => void navigate(routes.LOGIN_SALES))
  }

  // Smaller sidebar-width for sales portal
  useLayoutEffect(() => {
    const sidebarWidth = getComputedStyle(document.documentElement).getPropertyValue(SIDEBAR_WIDTH_VARIABLE)

    // Only update CSS variable if necessary
    if (sidebarWidth !== SIDEBAR_WIDTH_EXPANDED) {
      document.documentElement.style.setProperty(SIDEBAR_WIDTH_VARIABLE, SIDEBAR_WIDTH_EXPANDED)
    }
  }, [])

  useEffect(() => {
    if (query?.get('sessionExpired') === 'true') {
      popErrorToast(t(($) => $.sessionExpired))
    }
  }, [query])

  return (
    <EnergyTransitionDocumentContextProvider>
      <div className={classNames(styles['app-layout'], { [styles['has-navigation']]: !hideNavigation })}>
        {!hideNavigation && <SalesNavigation handleLogout={handleLogout} />}
        <main data-sales="true" className={classNames(styles.main, { [styles.wide]: hideNavigation })}>
          {loading || error ? <PageLoading isLoading={loading} showError={!!error} /> : <Outlet />}
        </main>
      </div>
    </EnergyTransitionDocumentContextProvider>
  )
}

export default SalesApp
